/**
 * a11y-cart-terms.js
 * Keyboard-accessibility helper for the cart drawer's Terms & Conditions checkbox.
 *
 * The third-party "Effective Apps - Terms and Conditions" app injects its
 * agree-checkbox (#effectiveAppsAgreeCB) + its own Checkout button into the cart
 * drawer AFTER the drawer renders, and the injection ORDER is timing dependent.
 * When the app places the terms block AFTER the checkout button, the drawer's
 * focus-trap treats the checkout button as the last focusable element and Tab
 * wraps back to the top before ever reaching the checkbox — so keyboard users
 * can tick neither the box (and the app blocks checkout until it is ticked).
 *
 * This helper pins the terms block to sit immediately BEFORE the checkout button
 * inside .cart-drawer__checkout-buttons, so the tab order is deterministic:
 *   ... shipping -> [terms checkbox] -> [terms link] -> Checkout -> wrap.
 * It also normalizes keyboard activation for the app checkbox: native checkboxes
 * toggle with Space, but many users press Enter once focus lands there. Treating
 * Enter as a click keeps the cart usable without changing mouse behavior.
 * It is idempotent and only moves the block when it is out of place.
 */
(function () {
  function reorderTerms() {
    var drawer = document.getElementById('cart-drawer');
    if (!drawer) return;
    var cb = drawer.querySelector('#effectiveAppsAgreeCB');
    if (!cb) return;
    var container = drawer.querySelector('.cart-drawer__checkout-buttons');
    if (!container) return;

    // The terms block is the nearest wrapper holding the checkbox (the app uses a <p>).
    var termsBlock = cb.closest('p') || cb.parentElement;
    if (!termsBlock) return;

    // Find the DIRECT child of the container that is (or contains) the actionable
    // checkout control, so it is a valid insertBefore reference node.
    var checkoutChild = Array.from(container.children).find(function (ch) {
      if (ch === termsBlock || ch.contains(termsBlock)) return false;
      return ch.matches('button, a.btn')
        || ch.querySelector('button[name="checkout"], button.btn--primary, a.btn--primary, button, a.btn');
    });
    if (!checkoutChild) return;

    // Already correctly positioned? (immediately before the checkout child)
    if (termsBlock.parentNode === container && termsBlock.nextElementSibling === checkoutChild) return;

    container.insertBefore(termsBlock, checkoutChild);
  }

  function normalizeTermsControl(evt) {
    var cb = evt.target && evt.target.closest && evt.target.closest('#effectiveAppsAgreeCB');
    if (!cb) return;

    if (evt.type === 'keydown' && (evt.key === 'Enter' || evt.code === 'Enter')) {
      evt.preventDefault();
      cb.click();
    }
  }

  function repairTermsLabel() {
    var drawer = document.getElementById('cart-drawer');
    if (!drawer) return;
    var cb = drawer.querySelector('#effectiveAppsAgreeCB');
    if (!cb) return;
    var label = cb.closest('p') && cb.closest('p').querySelector('label');
    if (label) label.setAttribute('for', 'effectiveAppsAgreeCB');
  }

  function init() {
    var drawer = document.getElementById('cart-drawer');
    if (!drawer) return;

    var scheduled = false;
    var observer = new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      // Defer + disconnect-around-move to avoid reacting to our own mutation.
      window.requestAnimationFrame(function () {
        scheduled = false;
        observer.disconnect();
        try {
          reorderTerms();
          repairTermsLabel();
        } finally {
          observer.observe(drawer, { childList: true, subtree: true });
        }
      });
    });

    observer.observe(drawer, { childList: true, subtree: true });
    reorderTerms();
    repairTermsLabel();
    drawer.addEventListener('keydown', normalizeTermsControl);
    document.addEventListener('on:cart-drawer:after-open', function () {
      reorderTerms();
      repairTermsLabel();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
